import React, { useState } from 'react';
import { Badge, Button, Form, Table } from 'react-bootstrap';
import { Plus, X } from 'lucide-react';
import {
  type PickerTarget,
  type ProductGroupTreeForm,
  type SectionPath,
  type SkuPickerSelection,
} from '../../utils/tree';
import {
  MAX_ATTRIBUTES,
  addAttributeToForm,
  addSubSectionToSectionInForm,
  appendNewTopLevelSectionToForm,
  applyPickerSelectionToForm,
  moveSectionWithinParentInForm,
  removeLastAttributeFromForm,
  removeLeafFromForm,
  removeSectionFromForm,
  updateAttributeNameInForm,
  updateSectionValueInForm,
} from '../../utils/treeMutations';
import { buildProductGroupEditorRows } from '../../utils/buildEditorRows';
import { SkuPicker } from '../SkuPicker';
import './ProductGroupTreeEditor.css';

const ATTRIBUTE_COL_WIDTH = 220;

interface ProductGroupTreeEditorProps {
  form: ProductGroupTreeForm;
  onChange: (next: ProductGroupTreeForm) => void;
  disabled?: boolean;
}

export const ProductGroupTreeEditor: React.FC<ProductGroupTreeEditorProps> = ({ form, onChange, disabled }) => {
  const [pickerTarget, setPickerTarget] = useState<PickerTarget | null>(null);
  const isDisabled = disabled ?? false;
  const totalColumns = form.attributes.length + 1;

  const updateAttributeName = (attributeIndex: number, value: string) =>
    onChange(updateAttributeNameInForm(form, attributeIndex, value));
  const addAttribute = () => onChange(addAttributeToForm(form));
  const removeLastAttribute = () => onChange(removeLastAttributeFromForm(form));
  const updateSectionValue = (path: SectionPath, value: string) =>
    onChange(updateSectionValueInForm(form, path, value));
  const moveSectionWithinParent = (path: SectionPath, direction: 1 | -1) =>
    onChange(moveSectionWithinParentInForm(form, path, direction));
  const removeSection = (path: SectionPath) => onChange(removeSectionFromForm(form, path));
  const removeLeaf = (parentPath: SectionPath, childIndex: number) =>
    onChange(removeLeafFromForm(form, parentPath, childIndex));
  const addSubSectionInSection = (path: SectionPath) =>
    onChange(addSubSectionToSectionInForm(form, path));
  const addNewTopLevelSection = () => onChange(appendNewTopLevelSectionToForm(form));
  const openPickerForSection = (path: SectionPath) => setPickerTarget({ kind: 'add', path });

  const handlePickerSelection = (selection: SkuPickerSelection) => {
    if (pickerTarget) onChange(applyPickerSelectionToForm(form, pickerTarget, selection));
    setPickerTarget(null);
  };

  const rows = buildProductGroupEditorRows({
    form,
    isDisabled,
    totalColumns,
    handlers: {
      updateSectionValue,
      moveSectionWithinParent,
      removeSection,
      removeLeaf,
      addSubSectionInSection,
      addNewTopLevelSection,
      openPickerForSection,
    },
  });

  const renderAttributeBadge = (depth: number) => (
    <span className="text-uppercase fw-semibold text-muted" style={{ fontSize: '0.7rem' }}>
      {form.attributes[depth] || `attribute ${depth + 1}`}
    </span>
  );

  return (
    <div className="pgte">
      <div className="mb-3">
        <Form.Label className="small fw-semibold mb-1">Attributes</Form.Label>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {form.attributes.map((attributeName, attributeIndex) => (
            <div key={attributeIndex} className="d-flex align-items-center gap-1">
              <Badge bg="dark" pill>{attributeIndex + 1}</Badge>
              <Form.Control
                size="sm"
                style={{ width: 160 }}
                type="text"
                value={attributeName}
                disabled={isDisabled}
                onChange={(e) => updateAttributeName(attributeIndex, e.target.value)}
                placeholder={attributeIndex === 0 ? 'e.g. color' : 'e.g. size'}
              />
            </div>
          ))}
          <Button
            variant="outline-dark"
            size="sm"
            onClick={addAttribute}
            disabled={isDisabled || form.attributes.length >= MAX_ATTRIBUTES}
            title="Add a deeper attribute (e.g. color → size)"
          >
            <Plus size={14} className="me-1" />
            Add attribute
          </Button>
          {form.attributes.length > 1 && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={removeLastAttribute}
              disabled={isDisabled}
              title="Remove the deepest attribute"
            >
              <X size={14} className="me-1" />
              Remove last
            </Button>
          )}
        </div>
        <div className="text-muted small mt-1">
          Add attributes based on available product attributes
        </div>
      </div>

      <div className="table-responsive">
        <Table className="pgte-table mb-0">
          <colgroup>
            {form.attributes.map((_, attributeIndex) => (
              <col key={attributeIndex} style={{ width: ATTRIBUTE_COL_WIDTH }} />
            ))}
            <col />
          </colgroup>
          <thead>
            <tr>
              {form.attributes.map((_, attributeIndex) => (
                <th key={attributeIndex} className="pgte-attr-cell">{renderAttributeBadge(attributeIndex)}</th>
              ))}
              <th>
                <span className="text-uppercase fw-semibold text-muted" style={{ fontSize: '0.7rem' }}>Product (SKU)</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {form.groups.length === 0 && (
              <tr>
                <td colSpan={totalColumns} className="text-center text-muted py-4">
                  <div className="mb-2">No sections yet.</div>
                  <Button
                    variant="dark"
                    size="sm"
                    onClick={addNewTopLevelSection}
                    disabled={isDisabled}
                  >
                    <Plus size={14} className="me-1" />
                    Add {form.attributes[0] || 'attribute'}
                  </Button>
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.key} className={row.rowClasses.join(' ')}>
                {row.cells.map((cell, columnIndex) => {
                  const cellClass = columnIndex < form.attributes.length ? 'pgte-attr-cell' : '';
                  return (
                    <td key={columnIndex} className={cellClass}>
                      {cell}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <SkuPicker
        show={pickerTarget !== null}
        onSelect={handlePickerSelection}
        onClose={() => setPickerTarget(null)}
      />
    </div>
  );
};
